import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { BookOpen, ChevronRight, Pencil, Save, X, RotateCcw, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function renderContent(text) {
  const lines = (text || "").split("\n");
  const out = [];
  let bullets = [];
  const flush = (key) => {
    if (bullets.length) {
      out.push(<ul key={`ul-${key}`} className="list-disc pl-5 space-y-1 my-1.5 text-white/70 text-sm">{bullets}</ul>);
      bullets = [];
    }
  };
  lines.forEach((raw, i) => {
    const line = raw.replace(/\s+$/, "");
    if (!line.trim()) { flush(i); return; }
    if (line.startsWith("- ")) { bullets.push(<li key={i}>{line.slice(2)}</li>); return; }
    flush(i);
    const isHeading = line === line.toUpperCase() && /[A-Z]/.test(line) && line.length < 60;
    if (isHeading) {
      out.push(<p key={i} className="font-display font-bold text-[#F2B705] text-sm tracking-wide mt-3 mb-0.5">{line}</p>);
    } else {
      out.push(<p key={i} className="text-white/70 text-sm leading-relaxed">{line}</p>);
    }
  });
  flush("end");
  return out;
}

export default function TabManual({ tabKey, token, defaultOpen = false, canEdit = true, initialManual = null }) {
  const [manual, setManual] = useState(initialManual);
  const [open, setOpen] = useState(defaultOpen);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => axios.get(`${API}/manuals`, { headers: { Authorization: `Bearer ${token}` } })
    .then((r) => setManual(r.data[tabKey] || { title: tabKey, content: "" }))
    .catch(() => {});

  useEffect(() => { if (!initialManual) load(); /* eslint-disable-next-line */ }, [tabKey, token]);

  const startEdit = () => { setDraft(manual?.content || ""); setEditing(true); setOpen(true); };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/manuals/${tabKey}`, { content: draft, title: manual?.title }, { headers: { Authorization: `Bearer ${token}` } });
      setManual(data);
      setEditing(false);
      toast.success("Manual updated.");
    } catch {
      toast.error("Could not save. Are you still logged in?");
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    if (!window.confirm("Reset this tab's guide to the original default text?")) return;
    try {
      const { data } = await axios.post(`${API}/manuals/${tabKey}/reset`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setManual(data);
      setEditing(false);
      toast.success("Reset to default.");
    } catch {
      toast.error("Could not reset.");
    }
  };

  if (!manual) return null;

  return (
    <div data-testid={`tab-manual-${tabKey}`} className="mb-6 border border-[#F2B705]/25 bg-[#F2B705]/[0.04] rounded-xl overflow-hidden">
      <button data-testid={`manual-toggle-${tabKey}`} onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between gap-2 px-4 py-3 text-left hover:bg-[#F2B705]/[0.06] transition-colors">
        <span className="flex items-center gap-2 font-semibold text-[#F2B705]">
          <BookOpen size={17} /> How this tab works
          {manual.title ? <span className="text-white/40 font-normal text-sm">· {manual.title}</span> : null}
        </span>
        <ChevronRight size={18} className={`text-[#F2B705] transition-transform ${open ? "rotate-90" : ""}`} />
      </button>

      {open && (
        <div className="px-4 pb-4 pt-1 border-t border-[#F2B705]/15">
          {editing ? (
            <div className="space-y-3 pt-3" data-testid={`manual-editor-${tabKey}`}>
              <textarea
                data-testid={`manual-textarea-${tabKey}`}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                rows={Math.min(24, Math.max(8, draft.split("\n").length + 1))}
                className="w-full bg-[#0A0A0A] border border-white/15 rounded-lg px-3 py-2.5 text-sm text-white/85 font-mono leading-relaxed focus:outline-none focus:border-[#F2B705]/60"
              />
              <p className="text-white/35 text-xs">Tip: A LINE IN ALL CAPS becomes a heading. A line starting with "- " becomes a bullet point.</p>
              <div className="flex flex-wrap gap-2">
                <button data-testid={`manual-save-${tabKey}`} onClick={save} disabled={saving}
                  className="flex items-center gap-1.5 bg-[#F2B705] text-black font-semibold text-sm px-4 py-2 rounded-md hover:bg-white transition-colors disabled:opacity-60">
                  {saving ? <Loader2 className="animate-spin" size={15} /> : <Save size={15} />} Save
                </button>
                <button data-testid={`manual-cancel-${tabKey}`} onClick={() => setEditing(false)}
                  className="flex items-center gap-1.5 border border-white/15 text-white/60 text-sm px-4 py-2 rounded-md hover:text-white transition-colors">
                  <X size={15} /> Cancel
                </button>
                <button data-testid={`manual-reset-${tabKey}`} onClick={reset}
                  className="flex items-center gap-1.5 border border-white/15 text-white/50 text-sm px-4 py-2 rounded-md hover:text-[#F2B705] transition-colors ml-auto">
                  <RotateCcw size={15} /> Reset to default
                </button>
              </div>
            </div>
          ) : (
            <div className="pt-3">
              <div className="space-y-0.5">{renderContent(manual.content)}</div>
              {canEdit && (
                <button data-testid={`manual-edit-${tabKey}`} onClick={startEdit}
                  className="mt-3 flex items-center gap-1.5 text-xs border border-[#F2B705]/40 text-[#F2B705] px-3 py-1.5 rounded-md hover:bg-[#F2B705] hover:text-black transition-colors">
                  <Pencil size={13} /> Edit this guide
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
